import { useCallback, useEffect, useRef, useState } from "react";
import { getFleetKpi } from "../../../../services/clearing/fleet-dashboard";
import type { KpiMonth, ScoreParameters } from "../../../../types/chargingStationDashboard";
import { isFleetFailure } from "../../../../types/chargingStationDashboard";

export interface FleetKpiState {
  loading: boolean;
  error: string | null;
  /** No day of the year has ever been computed — distinct from a year in which nothing happened. */
  neverComputed: boolean;
  /** The year's months up to the current one, oldest first. */
  months: KpiMonth[];
  parameters: ScoreParameters | null;
  reload: () => void;
}

const monthKey = (year: number, month: number) => `${year}-${String(month).padStart(2, "0")}`;

/**
 * The months of `year` the scorecard can ask for: January through December, or through the current
 * month for the running year. Later months have not happened, and asking for them only returns blanks.
 */
export const kpiYearRange = (year: number, today: Date = new Date()): { from: string; to: string } => {
  const lastMonth = year < today.getFullYear() ? 12 : today.getMonth() + 1;
  return { from: monthKey(year, 1), to: monthKey(year, lastMonth) };
};

/**
 * BOF-76 — the scorecard for a whole year in one request.
 *
 * One request serves both the chosen month's scorecard and the year's score trend (FR-053), so the two
 * can never disagree. Like `useFleetPeriods`, every new request clears what was shown and a response
 * that arrives after a newer request is discarded, so switching years quickly cannot leave last year's
 * scores on screen.
 */
export const useFleetKpi = (year: number): FleetKpiState => {
  const [state, setState] = useState<Omit<FleetKpiState, "reload">>({
    loading: true,
    error: null,
    neverComputed: false,
    months: [],
    parameters: null,
  });
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setState({ loading: true, error: null, neverComputed: false, months: [], parameters: null });

    const result = await getFleetKpi(kpiYearRange(year));
    if (requestId !== latestRequest.current) return; // a newer request has superseded this one

    if (isFleetFailure(result)) {
      setState({
        loading: false,
        error: result.neverComputed ? null : result.message,
        neverComputed: result.neverComputed,
        months: [],
        parameters: null,
      });
      return;
    }
    setState({
      loading: false,
      error: null,
      neverComputed: false,
      months: result.data.months ?? [],
      parameters: result.data.parameters ?? null,
    });
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
};
